import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Anchor, Lock, MoreHorizontal } from 'lucide-react';
import { FaEye, FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import RecallCheck from '../RecallCheck';
import ConfirmDialog from './ConfirmDialog';
import { getContrastColor } from '../../lib/colorContrast';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { isLocked } from '../../lib/premiumLock';
import {
  deleteTopic,
  updateTopic,
} from '../../store/workspacesSlice';
import { usePremium } from '../Subscription/PremiumProvider';

/**
 * Side panel for a selected spoke: why (relation) + gated hard detail + recall.
 * Unfiled topics use hard-detail copy and an Add to basket action instead of relation UI.
 * Locked template topics show a premium upsell instead of connection/details/recall.
 */
const TopicInspectPanel = ({
  topic,
  onAddToBasket,
  onMoveToBasket,
  onDeleted,
  onBeforeLeave,
  onRecallComplete,
  hasNextRecall = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isPremium, isLoading, openUpgradeModal } = usePremium();
  const currentWorkspaceId = useSelector(
    (state) => state.workspaces.currentWorkspace?.id
  );
  const [isDetailRevealed, setIsDetailRevealed] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const topicLocked = topic ? isLocked(topic, isPremium, isLoading) : false;

  useEffect(() => {
    setIsDetailRevealed(false);
    setIsDeleteOpen(false);
    setIsDeleting(false);
  }, [topic?.id]);

  useEffect(() => {
    if (!topicLocked || !topic?.id) return;
    captureConnectionsEvent('locked_item_opened', {
      topic_id: topic.id,
      basket_id: topic.basket_id || undefined,
    });
  }, [topicLocked, topic?.id, topic?.basket_id]);

  if (!topic) {
    return (
      <div className="h-full flex items-center justify-center p-6 text-sm text-neutral-500">
        Select a topic to inspect
      </div>
    );
  }

  const isUnfiled = !topic.basket_id;
  const highlightedConcept = topic.concepts?.find((c) => c.is_highlighted);
  const hardDetailText = highlightedConcept?.text;
  const tag = topic.tag || topic.tags;
  const tagColor = topic.tag?.color || topic.tags?.color;
  const workspaceId = topic.workspace_id || currentWorkspaceId;

  const handleRevealDetail = () => {
    setIsDetailRevealed(true);
    captureConnectionsEvent('connections_detail_revealed', {
      topic_id: topic.id,
      basket_id: topic.basket_id || undefined,
    });
  };

  const handleToggleAnchor = () => {
    if (!workspaceId) return;

    const nextIsAnchor = !topic.is_anchor;
    dispatch(
      updateTopic({
        topicId: topic.id,
        updates: { is_anchor: nextIsAnchor },
        workspaceId,
      })
    );
    captureConnectionsEvent('basket_anchor_topic_set', {
      basket_id: topic.basket_id,
      topic_id: topic.id,
      is_anchor: nextIsAnchor,
      source: 'user',
    });
  };

  const handleAddToBasket = () => {
    onAddToBasket?.(topic);
  };

  const handleEditItem = () => {
    if (!workspaceId) return;
    onBeforeLeave?.(topic);
    navigate(`/topic-detail/${topic.id}`, {
      state: {
        topic,
        basket: topic.basket || undefined,
        workspaceId,
        fromWorkspace: true,
      },
    });
  };

  const handleMoveToBasket = () => {
    onMoveToBasket?.(topic);
  };

  const handleOpenDelete = () => {
    setIsDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setIsDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!workspaceId || isDeleting) return;
    setIsDeleting(true);
    try {
      await dispatch(
        deleteTopic({ workspaceId, topicId: topic.id })
      ).unwrap();
      captureConnectionsEvent('topic_deleted', {
        topic_id: topic.id,
        basket_id: topic.basket_id || undefined,
        workspace_id: workspaceId,
        source: 'inspect_panel',
      });
      toast.success('Item deleted');
      setIsDeleteOpen(false);
      onDeleted?.(topic);
    } catch (error) {
      console.error('Error deleting topic:', error);
      toast.error('Could not delete the item. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpgrade = () => {
    openUpgradeModal('locked_item');
  };

  return (
    <div className="group/item-panel h-full flex flex-col px-4 pb-4 pt-2 sm:px-6 sm:pb-6 sm:pt-3 overflow-y-auto">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h2 className="text-lg sm:text-xl font-semibold text-neutral-900 min-w-0 pr-2">
          {topic.name}
        </h2>
        <Menu as="div" className="relative shrink-0">
          <MenuButton
            className="p-1 rounded-md text-neutral-500 hover:bg-neutral-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400 transition-opacity opacity-100 md:opacity-0 md:group-hover/item-panel:opacity-100 md:group-focus-within/item-panel:opacity-100 data-[open]:opacity-100"
            aria-label={`More actions for ${topic.name}`}
            aria-haspopup="menu"
          >
            <MoreHorizontal size={16} aria-hidden="true" />
          </MenuButton>
          <MenuItems
            anchor="bottom end"
            className="z-50 w-52 origin-top-right rounded-xl border-2 border-neutral-900 bg-white py-1 shadow-lg focus:outline-none"
          >
            {!topicLocked && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={handleEditItem}
                    className={`block w-full px-3 py-2 text-left text-sm ${
                      focus ? 'bg-neutral-100' : ''
                    } text-neutral-900`}
                  >
                    Edit item and details
                  </button>
                )}
              </MenuItem>
            )}
            {!topicLocked && topic.basket_id && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={handleMoveToBasket}
                    className={`block w-full px-3 py-2 text-left text-sm ${
                      focus ? 'bg-neutral-100' : ''
                    } text-neutral-900`}
                  >
                    Move to another basket
                  </button>
                )}
              </MenuItem>
            )}
            {!topicLocked && (
              <div className="my-1 border-t border-neutral-200" role="separator" />
            )}
            <MenuItem>
              {({ focus }) => (
                <button
                  type="button"
                  onClick={handleOpenDelete}
                  className={`block w-full px-3 py-2 text-left text-sm ${
                    focus ? 'bg-neutral-100' : ''
                  } text-cranberry-700`}
                >
                  Delete item
                </button>
              )}
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>

      {(tag || (!topicLocked && topic.basket_id)) && (
        <div className="mb-3 flex items-center gap-3 flex-wrap">
          {tag && (
            <span
              className="inline-block px-2 sm:px-3 py-0.5 rounded-full text-xs border border-neutral-900 max-w-[120px] whitespace-nowrap overflow-hidden text-ellipsis"
              style={{
                backgroundColor: `${(tagColor || '#981F2B')}CC`,
                color: getContrastColor(tagColor || '#981F2B'),
              }}
            >
              {tag.name || 'No Tag'}
            </span>
          )}
          {!topicLocked && topic.basket_id && (
            <div className="group/anchor relative inline-flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleToggleAnchor}
                className={`shrink-0 p-1 rounded-md focus:outline-none ${
                  topic.is_anchor
                    ? 'text-butter-500 hover:text-butter-600'
                    : 'text-neutral-400 hover:text-neutral-600'
                }`}
                aria-label={
                  topic.is_anchor
                    ? `Unset ${topic.name} as anchor`
                    : `Mark ${topic.name} as anchor`
                }
                aria-pressed={Boolean(topic.is_anchor)}
              >
                <Anchor
                  size={16}
                  fill={topic.is_anchor ? 'currentColor' : 'none'}
                  aria-hidden="true"
                />
              </button>
              <FaInfoCircle
                className="w-3.5 h-3.5 text-neutral-400 group-hover/anchor:text-neutral-600 cursor-help"
                aria-hidden="true"
              />
              <div className="invisible group-hover/anchor:visible absolute left-1/2 top-full z-50 mt-2 w-48 -translate-x-1/2 rounded-lg bg-neutral-900 px-3 py-2 text-xs leading-relaxed text-white shadow-lg pointer-events-none">
                Usually the first topic added to the basket, or the parent. Everything else
                connects back to it. Marking it helps you reconstruct the connections inside
                the basket at a glance.
                <div
                  className="absolute left-1/2 bottom-full h-2 w-2 -translate-x-1/2 translate-y-1 rotate-45 bg-neutral-900"
                  aria-hidden="true"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {topicLocked ? (
        <div className="flex flex-col items-center text-center py-6 px-2 space-y-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-neutral-100 text-neutral-500">
            <Lock size={18} aria-hidden="true" />
          </span>
          <p className="text-sm text-neutral-500 max-w-sm">
            Upgrade to see how this item connects, read the details and recall it.
          </p>
          <button
            type="button"
            onClick={handleUpgrade}
            className="mt-1 px-5 py-2.5 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cranberry-700"
          >
            Upgrade to Premium
          </button>
        </div>
      ) : !isDetailRevealed ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
          <button
            type="button"
            onClick={handleRevealDetail}
            className="w-full max-w-sm flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-neutral-400 bg-neutral-100 px-4 py-8 text-neutral-900 transition-colors duration-200 hover:border-neutral-500 hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700 focus-visible:ring-offset-2"
            aria-label={
              isUnfiled
                ? `Reveal the difficult detail for ${topic.name}`
                : `Reveal why ${topic.name} is here`
            }
            aria-expanded={false}
          >
            <span className="inline-flex items-center gap-2 text-sm font-medium sm:text-base">
              <FaEye size={15} aria-hidden="true" />
              {isUnfiled
                ? 'Reveal the difficult detail'
                : "Reveal why it's here"}
            </span>
            <span className="text-xs text-neutral-400">
              Take a moment before you look
            </span>
          </button>
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          {!isUnfiled && (
            topic.relation_description ? (
              <p className="text-sm italic text-neutral-600 mb-4 leading-relaxed">
                {topic.relation_description}
              </p>
            ) : (
              <p className="text-sm italic text-neutral-400 mb-4">
                No relationship description yet.
              </p>
            )
          )}

          {hardDetailText ? (
            <div className="relative pl-3 py-1 mb-2">
              <div
                className={`absolute left-0 top-0 bottom-0 w-0.5 ${
                  topic.is_ripe ? 'bg-[#C9982E]' : 'bg-blue-700'
                }`}
              />
              <p className="text-blue-700 text-sm leading-relaxed">{hardDetailText}</p>
            </div>
          ) : (
            <p className="text-sm text-neutral-400 mb-2">No highlighted detail on this topic.</p>
          )}

          {isUnfiled && onAddToBasket && (
            <button
              type="button"
              onClick={handleAddToBasket}
              className="mt-4 w-full rounded-lg border-2 border-neutral-900 bg-white px-3 py-2.5 text-sm font-semibold text-neutral-900 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-cranberry-700"
            >
              Add to basket
            </button>
          )}

          {topic.basket_id && (
            <div className="mt-2">
              <RecallCheck
                topicId={topic.id}
                topicName={topic.name}
                onRecallComplete={onRecallComplete}
                hasNext={hasNextRecall}
              />
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={isDeleteOpen}
        title={`Delete "${topic.name}"?`}
        body="This removes the item, its details and its recall history. This can't be undone."
        confirmLabel="Delete item"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default TopicInspectPanel;
